/**
 * "Switch organization", for a person who belongs to more than one.
 *
 * Each organization is its own world — its people, its data, and the role this person holds
 * there — so switching is a new session, not a filter: the server issues a token for the chosen
 * organization and the role its membership says, and the app starts over in it.
 */

import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeftRight, Building2, Check } from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { saveSession } from '../lib/auth'
import { useI18n } from '../lib/i18n'
import { Badge } from './primitives'

export function OrganizationSwitcher() {
  const { t } = useI18n()
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const ref = useRef<HTMLDivElement>(null)

  const { data: organizations } = useQuery({
    queryKey: ['organizations'],
    queryFn: api.auth.organizations,
    staleTime: 5 * 60_000,
    retry: false,
  })

  useEffect(() => {
    if (!open) return
    const away = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false)
    }
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    window.addEventListener('pointerdown', away, true)
    window.addEventListener('keydown', key)
    return () => {
      window.removeEventListener('pointerdown', away, true)
      window.removeEventListener('keydown', key)
    }
  }, [open])

  if (!organizations || organizations.length < 2) return null

  const current = organizations.find((o) => o.current)

  const choose = async (id: string) => {
    if (organizations.find((o) => o.id === id)?.current) {
      setOpen(false)
      return
    }
    setBusy(id)
    setError(null)
    try {
      saveSession(await api.auth.switchOrganization(id))
      // A full start in the chosen organization: every cache, subscription and live connection
      // belonged to the other one.
      window.location.assign('/dashboard')
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('error.generic'))
      setBusy(null)
    }
  }

  return (
    <div className="org-switcher" ref={ref}>
      <button
        type="button"
        className="org-switcher__button"
        onClick={() => setOpen(!open)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={t('org.switch')}
      >
        <Building2 size={14} aria-hidden />
        <span className="org-switcher__name truncate">{current?.name}</span>
        <ArrowLeftRight size={13} aria-hidden />
      </button>

      {open && (
        <div className="org-switcher__menu" role="menu" aria-label={t('org.switch')}>
          <div className="org-switcher__title tiny muted">{t('org.switchTitle')}</div>
          {organizations.map((organization) => (
            <button
              key={organization.id}
              type="button"
              role="menuitemradio"
              aria-checked={organization.current}
              className={`org-switcher__item${organization.current ? ' org-switcher__item--current' : ''}`}
              onClick={() => void choose(organization.id)}
              disabled={busy !== null}
            >
              <span className="stack" style={{ gap: 2, minWidth: 0, flex: 1 }}>
                <span className="small bold truncate">{organization.name}</span>
                <span className="row row--tight">
                  <Badge tone={organization.role === 'Owner' || organization.role === 'Admin' ? 'purple' : organization.role === 'EngineeringManager' ? 'accent' : 'neutral'}>
                    {t(`role.${organization.role}`)}
                  </Badge>
                  {organization.isDemo && <Badge tone="demo">{t('common.demoData')}</Badge>}
                </span>
              </span>
              {organization.current ? <Check size={15} aria-hidden /> : busy === organization.id ? <span className="tiny muted">…</span> : null}
            </button>
          ))}
          {error && <div className="field__error" role="alert">{error}</div>}
        </div>
      )}
    </div>
  )
}
